import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// APIベースURL（nginxプロキシ経由で/apiにアクセス）
const API_BASE_URL = '/api';

// Axiosインスタンスを作成
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // リフレッシュトークンは httpOnly Cookie で送るため必須
  withCredentials: true,
});

/**
 * アクセストークンは15分で切れる（リフレッシュトークン導入により短縮）。
 * 401 を受けたら /api/auth/refresh で更新し、元のリクエストを一度だけ再試行する。
 * これにより、ユーザーから見れば「ログインしっぱなし」になる。
 *
 * 同時に複数のリクエストが 401 になっても、リフレッシュは1回だけ走らせる
 * （並行して叩くとローテーションが競合し、再利用検知で全セッションが飛ぶ）。
 */
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const token: string = res.data.token;
        localStorage.setItem('token', token);
        if (res.data.user) {
          localStorage.setItem('user', JSON.stringify(res.data.user));
        }
        return token;
      })
      .catch(() => null)
      .finally(() => {
        // 次の 401 では改めてリフレッシュできるようにクリアする
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const tryRefreshSession = refreshAccessToken;

const onUnauthorized = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // 共用端末(キオスク)の画面はログインを前提としない。
  //
  // ⚠️ ここで転送すると、**過去に誰かがログインした端末で、そのセッションが
  // 切れた瞬間に打刻画面がログイン画面に変わる**。開店前に気づかなければ、
  // その日は誰も打刻できない。
  //
  // `/kiosk-devices`(管理画面)は通常のログインで守るので巻き込まない。
  if (/^\/kiosk(\/|$)/.test(window.location.pathname)) {
    return;
  }

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/** 認証系インターセプターを任意の axios インスタンスへ適用する。 */
export const attachAuthInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
      const url = original?.url || '';

      // リフレッシュ自体の 401 でループさせない
      const isAuthEndpoint = url.includes('/auth/refresh') || url.includes('/auth/login');

      // 共用端末(キオスク)は**端末トークンで認証する別系統**。
      // ここでの 401 は「この端末はまだ登録されていない」という意味であって、
      // ログインセッションの問題ではない。
      //
      // ⚠️ これを除外しないと、リフレッシュに失敗した時点で /login へ転送され、
      // **一度もログインしていない端末ではキオスク画面を開けなくなる**。
      // 店に置く端末にログインさせないための機能なので、ここで転送すると本末転倒になる。
      //
      // 先頭一致にしているのは、管理画面の `/kiosk-devices` を巻き込まないため
      // (あちらは通常のログインで守る。401 は本当にセッション切れを意味する)。
      const isKioskDeviceEndpoint = /^\/kiosk\//.test(url);

      if (
        error.response?.status === 401 &&
        original &&
        !original._retried &&
        !isAuthEndpoint &&
        !isKioskDeviceEndpoint
      ) {
        original._retried = true;
        const token = await refreshAccessToken();
        if (token) {
          original.headers = original.headers ?? {};
          (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
          return instance.request(original);
        }
        // リフレッシュも失敗＝セッション終了
        onUnauthorized();
      }

      return Promise.reject(error);
    }
  );
};

attachAuthInterceptors(api);
// 一部の画面は素の axios を直接使っているため、そちらにも同じ挙動を適用する
// （さもないとその画面だけ15分でエラーになる）
attachAuthInterceptors(axios);
axios.defaults.withCredentials = true;

export default api;
